import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { Package, Clock, CheckCircle2, Truck, XCircle, Pencil, ExternalLink } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { TrackingEventsDialogTrigger } from "@/components/internal/shared/TrackingEventsDialogTrigger";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useCarriers, useSamples, useSampleAdminStats } from "@/hooks/useSamples";
import { formatDate, formatStatusLabel } from "@/lib/format";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { getCarrierBadgeClass } from "@/lib/carrier-tag-colors";
import { getSampleStatusBadgeClass } from "@/lib/status-tag-colors";
import { useAppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

import { SampleEditDialog } from "./sample-edit-dialog";

function useColumns({
  onTrackingDelivered,
}: {
  onTrackingDelivered: (row: any) => Promise<void>;
}) {
  const { locale } = useAppLocale();
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.status()} />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getSampleStatusBadgeClass(row.original.status)}>
            {formatStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          label: m.status(),
          variant: "select",
          options: [
            { label: m.status_requested(), value: "pending" },
            { label: m.status_approved(), value: "approved" },
            { label: m.status_shipped(), value: "shipped" },
            { label: m.status_rejected(), value: "rejected" },
            { label: m.status_cancelled(), value: "cancelled" },
            { label: m.status_delivered(), value: "delivered" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "sampleType",
        accessorKey: "sampleType",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.type()} />,
        meta: { label: m.type(), placeholder: m.search_type_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "customer",
        accessorFn: (row) => row.user?.company || row.user?.fullName || row.contactEmail || "Unknown",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_customer()} />,
        meta: { label: m.samples_customer(), placeholder: m.samples_search_customer_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorFn: (row) => Boolean(row.user?.isEnterprise),
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.enterprise()} />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getEnterpriseBadgeClass(row.original.user?.isEnterprise)}>
            {getEnterpriseBadgeLabel(row.original.user?.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: m.enterprise(),
          variant: "select",
          options: [
            { label: m.customer_type_enterprise(), value: "true" },
            { label: m.customer_type_standard(), value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) =>
          filterValues.some((value: string) => value === String(Boolean(row.original.user?.isEnterprise))),
      },
      {
        id: "email",
        accessorFn: (row) => row.contactEmail || row.user?.email || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_email()} />,
        meta: { label: m.samples_email(), placeholder: m.samples_search_email_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "phone",
        accessorFn: (row) => row.contactPhone || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_phone()} />,
        cell: ({ row }) => <span className="text-sm">{row.original.contactPhone || "-"}</span>,
        meta: { label: m.samples_phone(), placeholder: m.samples_search_phone_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "address",
        accessorFn: (row) => {
          const a = row.shippingAddress;
          if (!a) return "";
          return [a.city, a.state].filter(Boolean).join(", ");
        },
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_address()} />,
        cell: ({ row }) => {
          const a = row.original.shippingAddress;
          if (!a) return <span className="text-muted-foreground">-</span>;
          return <span className="text-sm">{[a.city, a.state].filter(Boolean).join(", ")}</span>;
        },
        meta: { label: m.samples_address(), placeholder: m.samples_search_address_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "product",
        accessorFn: (row) => row.product?.name || row.cargo?.product?.name || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.product()} />,
        meta: { label: m.product(), placeholder: m.search_product_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "cargo",
        accessorFn: (row) => row.cargo?.ociNumber || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_cargo_oci()} />,
        meta: { label: m.cargo(), placeholder: m.search_cargo_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "carrier",
        accessorFn: (row) => row.carrier?.name || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_carrier()} />,
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getCarrierBadgeClass(
              row.original.carrier?.name,
              row.original.carrier?.shippeekCode,
            )}
          >
            {row.original.carrier?.name || "-"}
          </Badge>
        ),
        meta: { label: m.samples_carrier(), placeholder: m.samples_search_carrier_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "trackingNumber",
        accessorKey: "trackingNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_tracking_number()} />,
        cell: ({ row }) => {
          const trackingNumber = String(row.original.trackingNumber ?? "").trim();
          if (!trackingNumber) return <span className="text-muted-foreground">-</span>;
          if (isLocalPickupTracking(trackingNumber)) {
            return <span className="text-xs">{trackingNumber}</span>;
          }
          return (
            <TrackingEventsDialogTrigger
              trackingNumber={trackingNumber}
              carrierName={row.original.carrier?.name}
              carrierCode={row.original.carrier?.shippeekCode}
              triggerClassName="text-xs"
              onDelivered={
                normalizeStatus(row.original.status) === "delivered"
                  ? undefined
                  : () => onTrackingDelivered(row.original)
              }
            />
          );
        },
        meta: { label: m.samples_tracking(), placeholder: m.samples_search_tracking_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "label",
        accessorKey: "labelUrl",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_label()} />,
        cell: ({ row }) => {
          const url = row.original.labelUrl;
          if (!url) return <span className="text-muted-foreground">-</span>;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
              {m.view()} <ExternalLink className="h-3 w-3" />
            </a>
          );
        },
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples_requested()} />,
        cell: ({ row }) => {
          const ts = row.original.createdAt || row.original._creationTime;
          return ts ? <span className="text-sm">{formatDate(ts)}</span> : <span className="text-muted-foreground">-</span>;
        },
        meta: { label: m.samples_requested(), placeholder: m.search_date_placeholder(), variant: "text" },
      },
    ],
    [locale, onTrackingDelivered],
  );
}

function getTabs() {
  return [
    { value: "all", label: m.all() },
    { value: "requested", label: m.status_requested() },
    { value: "approved", label: m.status_approved() },
    { value: "shipped", label: m.status_shipped() },
    { value: "rejected", label: m.status_rejected() },
  ] as const;
}

type SampleTab = "all" | "requested" | "approved" | "shipped" | "rejected";

const REQUEST_STATUSES = new Set(["requested", "pending"]);
const APPROVED_STATUSES = new Set(["approved"]);

function normalizeStatus(status?: string) {
  return (status ?? "").toLowerCase();
}

function isLocalPickupTracking(value?: string) {
  const normalized = (value ?? "").trim().toLowerCase();
  return normalized === "local pick up" || normalized === "local pickup" || normalized === "pickup";
}

export default function InventorySamplesPage() {
  useAppLocale();
  const allSamples = useSamples(200);
  const carriers = useCarriers();
  const stats = useSampleAdminStats();
  const [activeTab, setActiveTab] = React.useState<SampleTab>("all");
  const [editSample, setEditSample] = React.useState<any>(null);
  const updateSampleMutation = useMutation(api.samples.updateSampleRequest);
  const bulkUpdateMutation = useMutation(api.samples.bulkUpdateSampleStatus);
  const [activeBulkAction, setActiveBulkAction] = React.useState<"approve" | "undoApprove" | "ship" | "reject" | null>(null);
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState("");
  const [rejectSampleIds, setRejectSampleIds] = React.useState<string[]>([]);
  const rejectClearSelectionRef = React.useRef<(() => void) | null>(null);
  const [shipOpen, setShipOpen] = React.useState(false);
  const [shipSampleIds, setShipSampleIds] = React.useState<string[]>([]);
  const shipClearSelectionRef = React.useRef<(() => void) | null>(null);
  const [shipForm, setShipForm] = React.useState({
    trackingNumber: "",
    carrierId: "",
    localPickup: false,
  });

  const handleTrackingDelivered = React.useCallback(async (row: any) => {
    const sampleRequestId = row._id ?? row.id;
    if (!sampleRequestId || normalizeStatus(row.status) === "delivered") return;

    try {
      await updateSampleMutation({
        sampleRequestId: sampleRequestId as any,
        status: "delivered",
      });
      toast.success("Sample marked delivered from tracking");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to mark sample delivered");
    }
  }, [updateSampleMutation]);

  const columns = useColumns({ onTrackingDelivered: handleTrackingDelivered });

  const filteredSamples = React.useMemo(() => {
    if (!allSamples) return [];
    if (activeTab === "all") return allSamples;
    return allSamples.filter((s: any) => {
      const status = String(s.status ?? "").toLowerCase();
      if (activeTab === "requested") return status === "requested" || status === "pending";
      if (activeTab === "rejected") return status === "rejected" || status === "cancelled";
      return status === activeTab;
    });
  }, [allSamples, activeTab]);

  const tabCounts = React.useMemo(() => ({
    all: stats?.total ?? 0,
    requested: stats?.pending ?? 0,
    approved: stats?.approved ?? 0,
    shipped: stats?.shipped ?? 0,
    rejected: (stats?.rejected ?? 0) + (stats?.cancelled ?? 0),
  }), [stats]);

  const getSelectedSampleIds = React.useCallback((selectedRows: any[]) => {
    return selectedRows.map((row) => row._id ?? row.id).filter(Boolean);
  }, []);

  const handleBulkStatusChange = React.useCallback(async (
    selectedRows: any[],
    newStatus: "approved" | "pending",
    action: "approve" | "undoApprove",
    successLabel: string,
    clearSelection: () => void,
  ) => {
    const ids = getSelectedSampleIds(selectedRows);
    if (ids.length === 0) {
      toast.error(m.samples_no_samples_selected());
      return;
    }
    setActiveBulkAction(action);
    try {
      const result = await bulkUpdateMutation({ sampleRequestIds: ids as any[], status: newStatus });
      const updated = result?.data?.updated ?? ids.length;
      toast.success(m.samples_bulk_success({ label: successLabel, count: updated }));
      clearSelection();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : m.samples_failed_to_update());
    } finally {
      setActiveBulkAction(null);
    }
  }, [bulkUpdateMutation, getSelectedSampleIds]);

  const openShipDialog = React.useCallback((selectedRows: any[], clearSelection: () => void) => {
    const ids = getSelectedSampleIds(selectedRows);
    if (ids.length === 0) {
      toast.error(m.samples_no_samples_selected());
      return;
    }

    const first = selectedRows[0];
    const firstTracking = String(first?.trackingNumber ?? "");
    const localPickup = isLocalPickupTracking(firstTracking);

    shipClearSelectionRef.current = clearSelection;
    setShipSampleIds(ids as string[]);
    setShipForm({
      trackingNumber: localPickup ? "" : firstTracking,
      carrierId: String(first?.carrierId ?? ""),
      localPickup,
    });
    setShipOpen(true);
  }, [getSelectedSampleIds]);

  const handleShipSelected = React.useCallback(async () => {
    if (shipSampleIds.length === 0) {
      toast.error(m.samples_no_samples_selected());
      return;
    }

    const trackingNumber = shipForm.localPickup
      ? "Local Pick Up"
      : shipForm.trackingNumber.trim();

    if (!trackingNumber) {
      toast.error(m.samples_add_tracking_or_local_pickup());
      return;
    }

    setActiveBulkAction("ship");
    try {
      const result = await bulkUpdateMutation({
        sampleRequestIds: shipSampleIds as any[],
        status: "shipped",
        trackingNumber,
        carrierId: shipForm.carrierId ? (shipForm.carrierId as any) : undefined,
      });
      const updated = result?.data?.updated ?? shipSampleIds.length;
      toast.success(m.samples_bulk_success({ label: m.samples_marked_shipped_label(), count: updated }));
      shipClearSelectionRef.current?.();
      setShipOpen(false);
      setShipSampleIds([]);
      setShipForm({ trackingNumber: "", carrierId: "", localPickup: false });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : m.samples_failed_to_mark_shipped());
    } finally {
      setActiveBulkAction(null);
    }
  }, [bulkUpdateMutation, shipForm, shipSampleIds]);

  const openRejectDialog = React.useCallback((selectedRows: any[], clearSelection: () => void) => {
    const ids = getSelectedSampleIds(selectedRows);
    if (ids.length === 0) {
      toast.error(m.samples_no_samples_selected());
      return;
    }
    rejectClearSelectionRef.current = clearSelection;
    setRejectSampleIds(ids as string[]);
    setRejectReason("");
    setRejectOpen(true);
  }, [getSelectedSampleIds]);

  const handleRejectSelected = React.useCallback(async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error(m.samples_provide_rejection_reason());
      return;
    }
    if (rejectSampleIds.length === 0) {
      toast.error(m.samples_no_samples_selected());
      return;
    }

    setActiveBulkAction("reject");
    try {
      const result = await bulkUpdateMutation({
        sampleRequestIds: rejectSampleIds as any[],
        status: "rejected",
        rejectionReason: reason,
      });
      const updated = result?.data?.updated ?? rejectSampleIds.length;
      toast.success(m.samples_bulk_success({ label: m.samples_rejected_label(), count: updated }));

      rejectClearSelectionRef.current?.();
      setRejectOpen(false);
      setRejectReason("");
      setRejectSampleIds([]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : m.samples_failed_to_reject());
    } finally {
      setActiveBulkAction(null);
    }
  }, [bulkUpdateMutation, rejectReason, rejectSampleIds]);

  const tabs = getTabs();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{m.samples_page_title()}</h1>
          <p className="text-muted-foreground mt-1">
            {m.samples_page_description()}
            {allSamples && allSamples.length > 0 ? (
              <span className="ml-1 font-medium text-foreground">{m.samples_total_count({ count: allSamples.length })}</span>
            ) : null}
          </p>
        </div>
      </div>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <StatCard icon={Package} iconColor="text-muted-foreground" value={tabCounts.all} label={m.samples_stat_total()} onClick={() => setActiveTab("all")} />
        <StatCard icon={Clock} iconColor="text-yellow-500" value={tabCounts.requested} label={m.status_requested()} onClick={() => setActiveTab("requested")} />
        <StatCard icon={CheckCircle2} iconColor="text-blue-500" value={tabCounts.approved} label={m.status_approved()} onClick={() => setActiveTab("approved")} />
        <StatCard icon={Truck} iconColor="text-green-500" value={tabCounts.shipped} label={m.status_shipped()} onClick={() => setActiveTab("shipped")} />
        <StatCard icon={XCircle} iconColor="text-red-500" value={tabCounts.rejected} label={m.status_rejected()} onClick={() => setActiveTab("rejected")} />
      </section>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{m.samples_page_title()}</CardTitle>
              <CardDescription>{m.samples_card_description()}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SampleTab)} className="mb-4">
            <TabsList>
              {tabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                  <Badge variant="secondary" className="ml-2">{tabCounts[tab.value]}</Badge>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {allSamples === undefined ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
          <InternalEntityDataTable
            data={filteredSamples.map((s: any) => ({ ...s, id: s._id || s.id }))}
            columns={columns}
            initialSorting={[{ id: "createdAt", desc: true }]}
            actionBar={({ selectedRows, clearSelection }) => (
              <DataTableFloatingBar
                selectedCount={selectedRows.length}
                onClear={clearSelection}
              >
                {selectedRows.length === 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={activeBulkAction !== null}
                    onClick={() => {
                      setEditSample(selectedRows[0]);
                      clearSelection();
                    }}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    {m.edit()}
                  </Button>
                )}
                {(() => {
                  const statuses = Array.from(
                    new Set(selectedRows.map((row) => normalizeStatus(row.status))),
                  );
                  const canApprove = statuses.length > 0
                    && statuses.every((status) => REQUEST_STATUSES.has(status));
                  const canReject = canApprove;
                  const canUndoApprove = statuses.length > 0
                    && statuses.every((status) => APPROVED_STATUSES.has(status));
                  const canMarkShipped = canUndoApprove;
                  const hasAnyAction = canApprove || canReject || canUndoApprove || canMarkShipped;

                  return (
                    <>
                      {canApprove ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={activeBulkAction !== null}
                          onClick={() =>
                            void handleBulkStatusChange(
                              selectedRows,
                              "approved",
                              "approve",
                              m.samples_approved_label(),
                              clearSelection,
                            )
                          }
                        >
                          {activeBulkAction === "approve" ? m.samples_approving() : m.samples_approve()}
                        </Button>
                      ) : null}
                      {canUndoApprove ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={activeBulkAction !== null}
                          onClick={() =>
                            void handleBulkStatusChange(
                              selectedRows,
                              "pending",
                              "undoApprove",
                              m.samples_moved_to_requested_label(),
                              clearSelection,
                            )
                          }
                        >
                          {activeBulkAction === "undoApprove" ? m.samples_updating() : m.samples_undo_approve()}
                        </Button>
                      ) : null}
                      {canMarkShipped ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={activeBulkAction !== null}
                          onClick={() => openShipDialog(selectedRows, clearSelection)}
                        >
                          {activeBulkAction === "ship" ? m.samples_updating() : m.samples_mark_shipped()}
                        </Button>
                      ) : null}
                      {canReject ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={activeBulkAction !== null}
                          onClick={() => openRejectDialog(selectedRows, clearSelection)}
                        >
                          {m.samples_reject()}
                        </Button>
                      ) : null}
                      {!hasAnyAction ? (
                        <span className="px-2 py-1 text-xs text-muted-foreground">
                          {m.samples_no_shared_action()}
                        </span>
                      ) : null}
                    </>
                  );
                })()}
              </DataTableFloatingBar>
            )}
          />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={rejectOpen}
        onOpenChange={(open) => {
          setRejectOpen(open);
          if (!open) {
            setRejectReason("");
            setRejectSampleIds([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{m.samples_reject_dialog_title()}</DialogTitle>
            <DialogDescription>
              {m.samples_reject_dialog_description({ count: rejectSampleIds.length })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="inventory-reject-reason">{m.samples_reason()}</Label>
            <Textarea
              id="inventory-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={m.samples_rejection_reason_placeholder()}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={activeBulkAction === "reject"}
              onClick={() => {
                setRejectOpen(false);
                setRejectReason("");
                setRejectSampleIds([]);
              }}
            >
              {m.cancel()}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleRejectSelected()}
              disabled={activeBulkAction === "reject" || !rejectReason.trim()}
            >
              {activeBulkAction === "reject" ? m.samples_rejecting() : m.samples_reject_samples()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={shipOpen}
        onOpenChange={(open) => {
          setShipOpen(open);
          if (!open) {
            setShipSampleIds([]);
            setShipForm({ trackingNumber: "", carrierId: "", localPickup: false });
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{m.samples_ship_dialog_title()}</DialogTitle>
            <DialogDescription>
              {m.samples_ship_dialog_description({ count: shipSampleIds.length })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="inventory-ship-carrier">{m.samples_carrier_optional()}</Label>
              <Select
                value={shipForm.carrierId || "none"}
                onValueChange={(value) =>
                  setShipForm((prev) => ({
                    ...prev,
                    carrierId: value === "none" ? "" : value,
                  }))
                }
              >
                <SelectTrigger id="inventory-ship-carrier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{m.samples_no_carrier()}</SelectItem>
                  {carriers?.map((carrier: any) => (
                    <SelectItem key={carrier._id} value={carrier._id}>
                      {carrier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inventory-ship-tracking">{m.samples_tracking_number()}</Label>
              <Input
                id="inventory-ship-tracking"
                value={shipForm.trackingNumber}
                onChange={(e) =>
                  setShipForm((prev) => ({ ...prev, trackingNumber: e.target.value }))
                }
                placeholder={m.samples_enter_tracking_placeholder()}
                disabled={shipForm.localPickup}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="inventory-ship-local-pickup"
                checked={shipForm.localPickup}
                onCheckedChange={(checked) => {
                  const isChecked = Boolean(checked);
                  setShipForm((prev) => ({
                    ...prev,
                    localPickup: isChecked,
                    trackingNumber: isChecked ? "" : prev.trackingNumber,
                  }));
                }}
              />
              <Label htmlFor="inventory-ship-local-pickup" className="font-normal">
                {m.samples_local_pickup_no_tracking()}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShipOpen(false);
                setShipSampleIds([]);
                setShipForm({ trackingNumber: "", carrierId: "", localPickup: false });
              }}
              disabled={activeBulkAction === "ship"}
            >
              {m.cancel()}
            </Button>
            <Button
              onClick={() => void handleShipSelected()}
              disabled={
                activeBulkAction === "ship"
                || (!shipForm.localPickup && !shipForm.trackingNumber.trim())
              }
            >
              {activeBulkAction === "ship" ? m.saving() : m.samples_mark_shipped()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SampleEditDialog
        open={!!editSample}
        onOpenChange={(open) => { if (!open) setEditSample(null); }}
        sample={editSample}
      />
    </div>
  );
}

function StatCard({ icon: Icon, iconColor, value, label, onClick }: {
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  value: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <Card className="cursor-pointer hover:bg-muted/50" onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Icon className={`h-8 w-8 ${iconColor}`} />
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
